import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert } from '@mui/material';
import RequirementCard from './RequirementCard';
import RequirementFilters, { type RequirementFiltersState } from './RequirementFilters';
import { ApiService, ApiEndpoints } from '../../../services/ApiService';
import { transformRequirements } from '../../../utils/dataTransform';

/**
 * Requirement interface
 */
interface Requirement {
  id: string | number;
  name: string;
  description?: string;
  status: 'completed' | 'in-progress' | 'not-started' | 'archived';
  controlId: string | number;
  controlName?: string;
  obligationId?: string | number;
  obligationName?: string;
  frameworkId?: string | number;
  frameworkName?: string;
  createdAt: string;
  updatedAt: string;
}


/**
 * RequirementList props interface
 */
interface RequirementListProps {
  controlId?: string | number;
  obligationId?: string | number;
  frameworkId?: string | number;
}

/**
 * Requirement List Component
 * 
 * Displays a list of requirements with filtering options
 * Optionally filters by control ID, obligation ID, or framework ID if provided
 */
export default function RequirementList({ controlId, obligationId, frameworkId }: RequirementListProps) {
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<RequirementFiltersState>({
    search: '',
    status: 'all',
    controlId: controlId ? String(controlId) : null,
    obligationId: obligationId ? String(obligationId) : null,
    frameworkId: frameworkId ? String(frameworkId) : null,
  });

  /**
   * Fetch requirements from API on component mount
   */
  useEffect(() => {
    const fetchRequirements = async () => {
      try {
        setLoading(true);
        // Set up query params
        const params: Record<string, any> = {};
        if (controlId) {
          params.controlId = controlId;
        }
        if (obligationId) {
          params.obligationId = obligationId;
        }
        if (frameworkId) {
          params.frameworkId = frameworkId;
        }
        
        const response = await ApiService.get<any[]>(ApiEndpoints.REQUIREMENTS.LIST, params);
        // Transform data from API format to frontend format
        const transformedData = transformRequirements(response);
        setRequirements(transformedData);
      } catch (err) {
        console.error('Error fetching requirements:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        
        // Fallback to mock data for development
        setRequirements([
          {
            id: 1,
            name: 'Document Approval Process',
            description: 'Create formal approval process for security policy documents',
            status: 'completed',
            controlId: 1,
            controlName: 'Security Policy Document',
            obligationId: 1,
            obligationName: 'Information Security Policy',
            frameworkId: 1,
            frameworkName: 'ISO 27001',
            createdAt: '2023-01-15T00:00:00Z',
            updatedAt: '2023-01-15T00:00:00Z',
          },
          {
            id: 2,
            name: 'Document Storage',
            description: 'Implement secure storage for policy documents',
            status: 'in-progress',
            controlId: 1,
            controlName: 'Security Policy Document',
            obligationId: 1,
            obligationName: 'Information Security Policy',
            frameworkId: 1,
            frameworkName: 'ISO 27001',
            createdAt: '2023-02-10T00:00:00Z',
            updatedAt: '2023-02-10T00:00:00Z',
          },
          {
            id: 3,
            name: 'DPIA Template Creation',
            description: 'Create a template for Data Protection Impact Assessments',
            status: 'not-started',
            controlId: 3,
            controlName: 'DPIA Template',
            obligationId: 3,
            obligationName: 'Data Protection Impact Assessments',
            frameworkId: 3,
            frameworkName: 'GDPR',
            createdAt: '2023-03-05T00:00:00Z',
            updatedAt: '2023-03-05T00:00:00Z',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchRequirements();
  }, [controlId, obligationId, frameworkId]);

  /**
   * Filter requirements based on filter criteria
   */
  const filteredRequirements = requirements.filter(requirement => {
    const matchesSearch = !filters.search || 
      requirement.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      (requirement.description && requirement.description.toLowerCase().includes(filters.search.toLowerCase()));
    
    const matchesStatus = filters.status === 'all' || requirement.status === filters.status;
    
    const matchesControl = !filters.controlId || String(requirement.controlId) === filters.controlId;
    
    const matchesObligation = !filters.obligationId || 
      (requirement.obligationId && String(requirement.obligationId) === filters.obligationId);
    
    const matchesFramework = !filters.frameworkId || 
      (requirement.frameworkId && String(requirement.frameworkId) === filters.frameworkId);
    
    return matchesSearch && matchesStatus && matchesControl && matchesObligation && matchesFramework;
  });

  /**
   * Handle filter changes
   */
  const handleFilterChange = (newFilters: Partial<RequirementFiltersState>) => {
    setFilters({ ...filters, ...newFilters });
  };

  /**
   * Loading state
   */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="requirement-list-loading">
        <CircularProgress />
      </Box>
    );
  }

  /**
   * Error state
   */
  if (error && requirements.length === 0) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading requirements: {error}
      </Alert>
    );
  }

  /**
   * Render component
   */
  return (
    <Box>
      <RequirementFilters 
        filters={filters} 
        onFilterChange={handleFilterChange}
        hideControlFilter={!!controlId}
        hideObligationFilter={!!obligationId}
        hideFrameworkFilter={!!frameworkId}
      />
      
      {filteredRequirements.length === 0 ? (
        <Alert severity="info">No requirements match the current filters</Alert>
      ) : (
        <Grid container spacing={3} data-testid="requirement-list">
          {filteredRequirements.map(requirement => (
            <Grid item xs={12} sm={6} md={4} key={requirement.id} data-testid={`requirement-item-${requirement.id}`}>
              <RequirementCard requirement={requirement} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

